/* External */
import {
  useMemo, memo, useRef, useCallback, useEffect,
} from 'react';
import PropTypes from 'prop-types';

/* Components */
import ScrollIntoViewIfFocused from '../ScrollIntoViewIfFocused';

/* Utils */
import { ScrollPosition, ScrollMode } from '../ScrollView';

function CardCarousel({
  focusKey,
  children,
  position = ScrollPosition.CENTER,
  mode = undefined,
  width = undefined,
  onWidthChange = undefined,
  cardPosition = undefined,
}) {
  const ref = useRef(undefined);
  const lastWidth = useRef(undefined);

  const style = useMemo(() => (width ? ({
    width: `${width}rem`,
    flex: `0 0 ${width}rem`,
  }) : ({})), [width]);

  const updateWidth = useCallback(() => {
    if (ref.current && onWidthChange && lastWidth.current !== ref.current.clientWidth) {
      lastWidth.current = ref.current.clientWidth;
      onWidthChange(lastWidth.current);
    }
  }, [onWidthChange]);

  const assignRef = useCallback((value) => {
    ref.current = value;
    updateWidth();
  }, [updateWidth]);

  useEffect(() => {
    updateWidth();
  });

  return (
    <ScrollIntoViewIfFocused
      focusKey={focusKey}
      className="dtv-html5-c-carousel__card"
      style={style}
      position={position}
      mode={mode}
      assignRef={assignRef}
      dataPosition={cardPosition}
    >
      {children}
    </ScrollIntoViewIfFocused>
  );
}

CardCarousel.propTypes = {
  focusKey: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  width: PropTypes.number,
  mode: PropTypes.oneOf([
    ScrollMode.ALWAYS,
    ScrollMode.IF_NEEDED,
  ]),
  position: PropTypes.oneOf([
    ScrollPosition.START,
    ScrollPosition.END,
    ScrollPosition.CENTER,
  ]),
  onWidthChange: PropTypes.func,
  cardPosition: PropTypes.number,
};

export default memo(CardCarousel);
